import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useController } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { driverSchema, type DriverFormData } from '@/validators/driver'
import { useDriver, useCreateDriver, useUpdateDriver } from '@/hooks/useDrivers'
import { FormSection } from '@/components/form/form-section'
import { CpfInput } from '@/components/form/cpf-input'
import { PhoneInput } from '@/components/form/phone-input'
import { AddressFields } from '@/components/form/address-fields'
import { BankFields } from '@/components/form/bank-fields'
import { DatePicker } from '@/components/form/date-picker'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'
import { cn } from '@/lib/utils'

const CNH_CATEGORIES = ['A', 'B', 'C', 'D', 'E'] as const

export function DriverFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  const { data: driver, isLoading: loadingDriver } = useDriver(id ?? '')
  const createMutation = useCreateDriver()
  const updateMutation = useUpdateDriver(id ?? '')
  const mutation = isEdit ? updateMutation : createMutation

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<DriverFormData>({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      name: '',
      cpf: '',
      rg: '',
      phone: '',
      email: '',
      driverType: 'OWN',
      cnhNumber: '',
      cnhCategory: [],
      cnhExpiry: '',
      zipCode: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      bankName: '',
      bankAgency: '',
      bankAccount: '',
      bankAccountType: '',
      pixKey: '',
    },
  })

  const { field: cnhCategoryField } = useController({ control, name: 'cnhCategory', defaultValue: [] })

  useEffect(() => {
    if (driver) {
      reset({
        name: driver.name,
        cpf: driver.cpf,
        rg: driver.rg ?? '',
        phone: driver.phone ?? '',
        email: driver.email ?? '',
        driverType: driver.driverType,
        cnhNumber: driver.cnhNumber ?? '',
        cnhCategory: (driver.cnhCategory ?? []) as ('A' | 'B' | 'C' | 'D' | 'E')[],
        cnhExpiry: driver.cnhExpiry ? driver.cnhExpiry.slice(0, 10) : '',
        zipCode: driver.zipCode ?? '',
        street: driver.street ?? '',
        number: driver.number ?? '',
        complement: driver.complement ?? '',
        neighborhood: driver.neighborhood ?? '',
        city: driver.city ?? '',
        state: driver.state ?? '',
        bankName: driver.bankName ?? '',
        bankAgency: driver.bankAgency ?? '',
        bankAccount: driver.bankAccount ?? '',
        bankAccountType: (driver.bankAccountType as 'corrente' | 'poupanca' | '') ?? '',
        pixKey: driver.pixKey ?? '',
      })
    }
  }, [driver, reset])

  async function onSubmit(data: DriverFormData) {
    try {
      await mutation.mutateAsync(data)
      toast.success(isEdit ? 'Motorista atualizado!' : 'Motorista cadastrado!')
      navigate('/drivers')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
      toast.error(msg ?? 'Erro ao salvar motorista')
    }
  }

  if (isEdit && loadingDriver) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/drivers')}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? 'Editar motorista' : 'Novo motorista'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? driver?.name : 'Preencha os dados do motorista'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Dados pessoais */}
        <FormSection title="Dados pessoais">
          <div className="space-y-1.5">
            <Label htmlFor="name">Nome completo *</Label>
            <Input
              id="name"
              aria-invalid={!!errors.name}
              {...register('name')}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CpfInput control={control} name="cpf" />
            <div className="space-y-1.5">
              <Label htmlFor="rg">RG</Label>
              <Input id="rg" {...register('rg')} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <PhoneInput control={control} name="phone" />
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="driverType">Tipo de vínculo *</Label>
            <select
              id="driverType"
              {...register('driverType')}
              aria-invalid={!!errors.driverType}
              className={cn(
                'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                errors.driverType && 'border-destructive',
              )}
            >
              <option value="OWN">Próprio</option>
              <option value="AGGREGATE">Agregado</option>
            </select>
            {errors.driverType && (
              <p className="text-xs text-destructive">{errors.driverType.message}</p>
            )}
          </div>
        </FormSection>

        {/* CNH */}
        <FormSection title="CNH">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="cnhNumber">Número da CNH</Label>
              <Input id="cnhNumber" {...register('cnhNumber')} />
            </div>
            <div className="space-y-1.5">
              <Label>Categoria(s)</Label>
              <div className="flex flex-wrap gap-2 pt-1">
                {CNH_CATEGORIES.map((cat) => {
                  const checked = cnhCategoryField.value?.includes(cat) ?? false
                  return (
                    <label
                      key={cat}
                      className={cn(
                        'flex cursor-pointer select-none items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors',
                        checked
                          ? 'border-primary bg-primary/10 text-primary font-medium'
                          : 'border-input text-muted-foreground hover:border-primary/50',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={(e) => {
                          const current = cnhCategoryField.value ?? []
                          cnhCategoryField.onChange(
                            e.target.checked
                              ? [...current, cat].sort()
                              : current.filter((c) => c !== cat),
                          )
                        }}
                      />
                      {cat}
                    </label>
                  )
                })}
              </div>
            </div>
            <DatePicker control={control} name="cnhExpiry" label="Validade" />
          </div>
        </FormSection>

        {/* Endereço */}
        <AddressFields
          register={register}
          errors={errors}
          setValue={setValue}
          control={control}
          zipCodeName="zipCode"
          streetName="street"
          numberName="number"
          complementName="complement"
          neighborhoodName="neighborhood"
          cityName="city"
          stateName="state"
        />

        {/* Dados bancários */}
        <BankFields
          register={register}
          errors={errors}
          bankNameName="bankName"
          bankAgencyName="bankAgency"
          bankAccountName="bankAccount"
          bankAccountTypeName="bankAccountType"
          pixKeyName="pixKey"
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/drivers')}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <LoadingButton
            type="submit"
            loading={mutation.isPending}
            loadingText="Salvando…"
          >
            {isEdit ? 'Salvar alterações' : 'Cadastrar motorista'}
          </LoadingButton>
        </div>
      </form>
    </div>
  )
}
